import { FileDBValue } from '../data'
import { UserGroup, WithMetadata } from '../shared'
import { ConsentFormTemplate } from '../templates'
import { Address, AnyObject, Hospital, Physician, WithAlternate } from '../utils'

export interface BilledCharge {
  amount: number
}
export type BilledCharges = Record<string, number>

// §3.3: Determines how the delivery/birthing center module is used for this practice.
// Mode 1 = midwife-owned practice (EHR is the complete record).
// Mode 2 = birthing center adopts EHR as facility system (must meet NYS Article 28 — §7.5).
// Mode 3 = supplemental alongside facility's own system — deferred to V2.
export type BirthingCenterMode = 'mode-1' | 'mode-2'

// §6.9: Drives which billing workflows are available for this practice.
export type FacilityType =
  | 'homebirth-practice'
  | 'birthing-center'
  | 'hospital-based'
  | 'mobile-clinic'
  | 'other'

export interface Practice {
  id: string
  name: string
  // §3.3: Operational mode for birthing center / delivery module.
  birthing_center_mode?: BirthingCenterMode
  logoFile?: FileDBValue
  description?: string
  fname: string
  createdOn?: number
  createdBy?: string
  email: string
  abilityId?: string
  isInactive: boolean
  prm?: number
  npi?: string
  lname: string
  isInvite?: boolean
  rank: number
  zipCode?: string
  billedCharges?: BilledCharges
  // §6.9: Practice/facility type — determines which billing workflows are available.
  facilityType?: FacilityType
  // §6.4.2: Admin-configurable notification thresholds (days from DOS). Defaults: 45, 52, 59.
  notificationThresholds?: {
    firstNotice: number
    criticalNotice: number
    billingAlert: number
  }
}

export interface License {
  number: string
  state: string
  credentials: Record<string, boolean>
}

export interface MedicaidId {
  state: string
  id: string
}

export interface Provider {
  fname?: string
  lname?: string
  npi?: string
  medicaidIds?: Array<MedicaidId>
  inActive?: boolean
}

// TODO: Separate Referring Provider info to its own type
// TODO: Separate addresses to their own type

export type PracticeAccessRole = 'admin' | 'super-admin' | 'user'
export interface PracticeUserAccess {
  role: PracticeAccessRole
  email: string
  fname: string | null
  lname: string | null
  nickname: string | null
  addedOn: number
  adminColor?: string
  adminColorNotes?: string
  adminColorUpdatedBy?: string
  adminColorUpdatedOn?: number
  addedBy: string
}
export type PracticeUsers = Record<string, PracticeUserAccess>
export interface PracticeAccess {
  sendInvoicesTo?: string | null
  users?: PracticeUsers
}

// VISIBLE TO ADMINS AND PRACTICE MEMBERS ONLY - Needs to be implemented/migrated from admin data
export interface ProtectedPracticeData {
  // serviceAddress? : Address
  affiliatedHospitals?: Array<Hospital>
  npi?: string
  isGroup?: boolean
  group?: {
    name?: string
    npi?: string
    medicaidIds?: Array<MedicaidId>
  }
  fax?: WithAlternate<string>
  claimsAddress: string
  servicingAddresses?: Array<Address>
  medicaidIds?: Array<MedicaidId>
  addressNotes?: string
  phone?: string
  licenses?: Array<License>
}

export type PracticeConsentForm = WithMetadata<
  ConsentFormTemplate & {
    // isActive: boolean
    rank: number
  }
>

export interface AdminPracticeData {
  id: string
  specialty?: string
  affiliatedHospitals?: Array<Hospital>
  // npi?: string
  isGroup?: boolean
  group?: {
    name?: string
    npi?: string
    medicaidIds?: Array<MedicaidId>
  }
  fax?: WithAlternate<string>
  claimsAddress: string
  servicingAddresses?: Array<Address>
  paymentsAddress?: Address
  corporateAddress?: Address
  payToAddress?: Address
  // on provider log in, tax ID should show unless its an SSN
  taxId?: string
  taxIdIsSSN?: boolean
  feinTaxClass?: string
  ssn?: string
  fname?: string
  lname?: string
  dob?: number
  medicaidIds?: Array<MedicaidId>
  email?: WithAlternate<string>
  notes?: string
  addressNotes?: string
  phone?: string
  licenses?: Array<License>
  prmNotes?: string
  retainer?: number
  retainerNotes?: string
  collaborativePhysicians?: Physician[]
  renderingProviders?: Array<Provider>
  acceptedPaymentMethods?: string
  paymentInfo?: string
  redFlagged?: boolean
  redFlaggedReason?: string
  // §6.8: POCUS credentialing status — checked on every POCUS claim.
  pocusCredentialing?: {
    certified: boolean
    expiresOn?: string // ISO date string YYYY-MM-DD
  }
  // §6.4.2: Provider Billing Authorization — must be on file before billing team can submit under draft authorization.
  providerBillingAuthorization?: {
    onFile: boolean
    signedOn?: string // ISO date string YYYY-MM-DD
    documentUrl?: string
    notes?: string
  }
  // §A (Provider Identity): Supervising provider of record for HCFA Box 17 / claim qualifier.
  supervisingProviderOfRecord?: {
    userId: string
    npi?: string
  } | null
}

export type PracticeWithAdmin = Omit<Practice, 'email'> & Partial<AdminPracticeData>

export interface SubmittedAuthentication {
  practiceId: string
  userId: string
  signatureText: string
}

export interface BaseAuthentication<FormData extends AnyObject> extends SubmittedAuthentication {
  ip: string
  formData: FormData
  authenticatedOn: number
  userGroup: UserGroup
  pdfStoragePath?: string
  providerFirst: string
  providerLast: string
}

export interface ProviderAuthentication<FormData extends AnyObject>
  extends BaseAuthentication<FormData> {
  previousVersions: BaseAuthentication<FormData>[]
}

export interface ICD10 {
  code: string
  description: string
  keywords?: string[]
  notes?: string
  prefix?: string
}

export interface ICD10Group {
  name: string
  codes: ICD10[]
}

export const isIcd10Group = (item: ICD10 | ICD10Group): item is ICD10Group =>
  (item as ICD10Group).codes !== undefined

export interface CPTCode {
  code: string
  icd10: ICD10[]
  description: string
  notes?: string
}

export interface CPTCodeGroup {
  name: string
  codes: CPTCode[]
}

export const isCptCodeGroup = (item: CPTCode | CPTCodeGroup): item is CPTCodeGroup =>
  (item as CPTCodeGroup).codes !== undefined
